import React from 'react'
import { Box, IconButton, List, Paper, Tooltip, Typography } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { visibleWidgetGroups } from '../widgetTaxonomy'
import WidgetPaletteCard from './WidgetPaletteCard'

interface WidgetPaletteProps {
  isManager?: boolean
  /** Shown under the "Widgets" heading. Defaults to the builder's wording. */
  subtitle?: string
  /** When provided, the palette shows a collapse chevron and, collapsed,
   * folds to a slim rail that reopens on click. The open/closed state lives
   * with the caller so each surface can persist it as it sees fit. */
  collapsed?: boolean
  onToggleCollapse?: () => void
}

/**
 * Side-pane widget palette, shared by the template builder (#971) and the
 * dashboard editor's edit mode. Same taxonomy, order and subtitles as the Edit
 * Widget modal's picker (#966) — one source of truth in `widgetTaxonomy.tsx` —
 * but each tile is draggable instead of a click target, because on both
 * surfaces a widget is placed by dragging it onto the canvas.
 */
const WidgetPalette: React.FC<WidgetPaletteProps> = ({ isManager, subtitle, collapsed, onToggleCollapse }) => {
  const groups = visibleWidgetGroups(isManager)

  if (collapsed && onToggleCollapse) {
    return (
      <Paper
        elevation={0}
        onClick={onToggleCollapse}
        sx={{
          width: 36, minWidth: 36, borderRight: 1, borderColor: 'divider', height: '100%',
          display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1, gap: 1,
          cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <Tooltip title="Show widgets" placement="right">
          <IconButton size="small" aria-label="Show widget palette"><ChevronRightIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Typography
          variant="overline"
          sx={{ writingMode: 'vertical-rl', fontWeight: 700, color: 'text.secondary', letterSpacing: 1 }}
        >
          Widgets
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper elevation={0} sx={{ width: 240, minWidth: 240, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Widgets</Typography>
          <Typography variant="caption" color="text.secondary">{subtitle || 'Drag onto the canvas'}</Typography>
        </Box>
        {onToggleCollapse && (
          <Tooltip title="Hide widgets" placement="right">
            <IconButton size="small" onClick={onToggleCollapse} aria-label="Hide widget palette">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <List dense sx={{ px: 1, flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {groups.map(group => (
          <React.Fragment key={group.label}>
            <Typography
              variant="overline"
              sx={{ px: 1, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem' }}
            >
              {group.label}
            </Typography>
            {group.widgets.map(widget => (
              <WidgetPaletteCard key={widget.type} widget={widget} />
            ))}
          </React.Fragment>
        ))}
      </List>
    </Paper>
  )
}

export default WidgetPalette
